import { useEffect, useState, type FormEvent } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import api from '@/lib/api'

interface RenameManualAccountDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  accountId: number
  currentName: string
  onRenamed: (name: string) => void
}

export function RenameManualAccountDialog({
  open,
  onOpenChange,
  accountId,
  currentName,
  onRenamed,
}: RenameManualAccountDialogProps) {
  const [name, setName] = useState(currentName)
  const [error, setError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (open) {
      setName(currentName)
      setError(null)
      setIsSaving(false)
    }
  }, [open, currentName])

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setError(null)

    const trimmed = name.trim()
    if (!trimmed) {
      setError('Account name cannot be empty.')
      return
    }

    setIsSaving(true)
    try {
      const response = await api.put(`/manual_accounts/${accountId}`, {
        name: trimmed,
      })
      onRenamed(response.data.account?.name ?? trimmed)
      onOpenChange(false)
    } catch (err) {
      console.error('Failed to rename account:', err)
      setError('Unable to rename account. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Rename Account</DialogTitle>
        </DialogHeader>
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="grid gap-2">
            <Label htmlFor="rename-name">Account name</Label>
            <Input
              id="rename-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              autoFocus
            />
          </div>

          {error && <p className="text-sm text-danger">{error}</p>}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
